/**
 * @fileoverview Tracking mapping dialog component for order tag management.
 * Handles the mapping and configuration of tracking-related tags when orders
 * are updated with specific error or processing tags.
 *
 * @author Web Analysis Team
 * @version 1.0.0
 */

import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Create, SaveButton, SelectInput, SimpleForm, TextInput, required } from 'react-admin';
import { Button, Chip, Grid, Typography } from '@mui/material';
import MDialog from '../../../../../components/controls/MDialog';
import { IData } from '../../../../../types';

/**
 * Tags that require tracking mapping configuration when added to orders.
 * @type {Array<string>}
 * @constant
 */
const tagsTracking = ['Processing Mistake', 'Shipping Mistake', 'Mapping Error'];

/**
 * Extracts newly added tags by comparing current tags with previous tags.
 *
 * @param {Object} order - Order object containing current tags
 * @param {string} oldTags - Comma-separated string of previous order tags
 * @returns {Array<string>} Array of newly added tags
 */
function getTagsTracking (order, oldTags) {
    let newTags = order.tags;
    const arrCurrentTags = (oldTags || ',').split(',');
    const arrTags = (newTags || ',').split(',');
    const addedTags = arrTags.filter(tag => !arrCurrentTags.includes(tag));
    return addedTags;
}

/**
 * Determines if tracking mapping configuration should be displayed for an order.
 *
 * @param {Object} order - Order object to check for tracking requirements
 * @param {string} oldTags - Previous tags to compare against current tags
 * @returns {boolean} True if tracking mapping is required, false otherwise
 */
export const onConfigTrackingMapping = (order, oldTags) => {
    const addedTags = getTagsTracking(order, oldTags);
    return tagsTracking.some(x => addedTags.includes(x));
};

/** Props for {@link FormContent}. */
interface FormContentProps {
    /** Dialog data: the tracking record being edited. */
    data: IData;
    /** Called to close the dialog. */
    handleClose: (...args: any[]) => void;
}

const FormContent: React.FC<FormContentProps> = ({ data, handleClose }) => {
    const { record } = data;
    const { setValue } = useFormContext();
    const [otherRequired, setOtherRequired] = useState<IData>({});

    const defaultErrors = [
        'Incorrect event name',
        'Incorrect event time',
        'Incorrect event date',
        'Incorrect event type (not actual event)',
        'Other'
    ];

    const trimmedRequired = [
        required(),
        (value) => {
            if (value && typeof value === 'string' && value.trim() === '') {
                return 'This field cannot be null or contain only whitespace.';
            }
            return undefined;
        }
    ];

    useEffect(() => {
        if (!record.isMapping) setOtherRequired({ validate: trimmedRequired });
    }, [record]);

    const onChangeError = (e) => {
        if (e) {
            const { value } = e.target;
            if (value === 'Other') {
                setValue('otherError', '');
                setOtherRequired({ validate: trimmedRequired });
                return;
            }
        }
        setValue('otherError', '');
        setOtherRequired({});
    };

    return (
        <div style={{ width: '100%' }}>
            <Grid container size={12} style={{ marginTop: -20, marginBottom: '10px' }}>
                <span><b>Tag:&nbsp;</b>
                    {record.tag?.split(',').map(m => <Chip label={m} color={m.includes('Error') ? 'error' : 'warning'} />)}
                </span>
            </Grid>
            {record.isMapping && (
                <Grid container>
                    <SelectInput source="error" validate={required()} choices={defaultErrors.map((x: any) => ({ id: x, name: x }))} onChange={onChangeError} translateChoice={false} resettable />
                </Grid>
            )}
            <Grid container style={{ marginTop: record.isMapping ? -10 : 0 }}>
                <TextInput source="otherError" {...otherRequired} multiline minRows={2} resettable />
            </Grid>
            {!record.isMapping && (
                <Grid container style={{ marginTop: -25 }}>
                    <Grid size={4} style={{ alignContent: 'center' }}>
                        <Typography>Link with Order: </Typography>
                    </Grid>
                    <Grid size={8}>
                        <TextInput source="orderNumber" resettable />
                    </Grid>
                </Grid>
            )}
            <Grid container justifyContent={'flex-end'} spacing={1} style={{ marginBottom: -30 }}>
                <Grid>
                    <SaveButton variant="contained" color="primary" label={'Save Order'} />
                </Grid>
                <Grid>
                    <Button onClick={handleClose} variant='outlined' aria-label={'m_isOpen'}>Close</Button>
                </Grid>
            </Grid>
        </div>
    );
};

/** Props for {@link TrackingMappingPopup}. */
interface TrackingMappingProps {
    /** Data containing order and dialog state information. */
    data: IData;
    /** Function to call when closing the dialog. */
    onClose: (...args: any[]) => void;
}

/**
 * Tracking mapping popup component: prompts for the reason behind an error/mistake tag.
 * @param props - See {@link TrackingMappingProps}.
 */
const TrackingMappingPopup: React.FC<TrackingMappingProps> = ({ data, onClose }) => {
    const { order } = data;
    const { m_isOpen, m_data, callback } = data.dlgState;
    const [trackingItem, setTrackingItem] = useState<IData>({});

    useEffect(() => {
        if (m_isOpen && order) {
            let addedTags = getTagsTracking(order, m_data.oldTags);
            setTrackingItem({
                error: '',
                otherError: '',
                orderNumber: '',
                tag: addedTags.filter((x: any) => tagsTracking.find((y: any) => y === x)).join(','),
                isMapping: addedTags.some(x => x === 'Mapping Error')
            });
        }
    }, [m_isOpen, order]);

    const onSubmit = (data) => {
        let defaultError = data.error?.trim();
        let msgError = data.otherError?.trim();
        if (trackingItem.isMapping) {
            msgError = defaultError;
            if (data.otherError) msgError += ` - Detail: ${data.otherError}.`;
        } else { // for mistaken
            if (data.orderNumber) {
                msgError += ` (Link with order: ${data.orderNumber})`;
            }
        }
        callback(msgError);
        onClose(null, 'm_isOpen', false);
    };

    return (
        <Create record={trackingItem}>
            <MDialog
                title={`TRACKING ORDER #${order.order_number}`}
                maxWidth={'sm'}
                open={m_isOpen}
                handleClose={onClose}
                name={'m_isOpen'}
                footer={false}
                msgLoading={'Saving...'}
                className={'order-dialog'}
            >
                <SimpleForm defaultValues={order.orderProcessTracking || {}} toolbar={false} mode="onBlur" reValidateMode="onBlur" onSubmit={onSubmit}>
                    <FormContent
                        handleClose={onClose}
                        data={{ record: trackingItem }}
                    />
                </SimpleForm>
            </MDialog>
        </Create>
    );
};

export default TrackingMappingPopup;
